import { BaseResponseSchema, getRequiredMessage } from '@monetize/types/common';
import { boolean, string, z } from 'zod';
import { AccountSummarySchema } from './account.types';
import { AddressSchema } from './address.types';
import { ContactRespSchema } from './contact.types';
import { GetCreditSchema } from './credit.types';
import { CustomFieldRecordSchema } from './customFields.types';
import { InvoiceCreditNoteSummarySchema } from './invoice.types';

export enum CreditNoteTypeEnum {
  REFUND = 'REFUND',
  ADJUSTMENT = 'ADJUSTMENT',
  WRITE_OFF = 'WRITE_OFF',
}
export const CreditNoteTypeEnumZ = z.enum(CreditNoteTypeEnum);

export enum CreditNoteStatusEnum {
  CANCELED = 'CANCELED',
  APPLIED = 'APPLIED',
  VOID = 'VOID',
}
export const CreditNoteStatusEnumZ = z.enum(CreditNoteStatusEnum);

export enum CreditNoteRefundReasonEnum {
  BILLING_ERROR = 'BILLING_ERROR',
  SERVICE_ISSUE = 'SERVICE_ISSUE',
  CANCELLATION = 'CANCELLATION',
  ADJUSTMENT = 'ADJUSTMENT',
  OTHER = 'OTHER',
}
export const CreditNoteRefundReasonEnumZ = z.enum(CreditNoteRefundReasonEnum);

export const CreditNoteItemSchema = z.object({
  id: z.string(),
  creditNoteId: z.string().nullable(),
  invoiceItemId: z.string().nullable(),
  description: z.string(),
  periodStartDate: z.string(),
  periodEndDate: z.string(),
  quantity: z.string(),
  amount: z.number(),
});

export type ICreditNoteItemSchema = z.infer<typeof CreditNoteItemSchema>;

export const CreditNoteMetadataSchema = z.object({
  source: z.enum(['quote', 'invoice']),
  sourceId: z.string(),
  action: z.enum(['DOWNGRADE', 'CREDIT_REBILL', 'CREDIT_NOTE']),
});

export const CreditNoteSchema = BaseResponseSchema.extend({
  accountId: z.string(),
  accountSummary: AccountSummarySchema,
  billGroupId: z.string(),
  currency: z.string(),
  invoiceId: z.string().nullish(),
  invoice: InvoiceCreditNoteSummarySchema.nullish(),
  creditId: z.string().nullable(),
  credit: GetCreditSchema,
  description: z.string().nullish(),
  type: CreditNoteTypeEnumZ,
  status: CreditNoteStatusEnumZ,
  amount: z.number(),
  amountWithoutTax: z.number(),
  tax: z.number(),
  taxCalculationStatus: z.string().nullish(),
  taxCalculationError: z.string().nullish(),
  refundable: boolean(),
  creditNoteItems: CreditNoteItemSchema,
  customFields: z.record(z.string(), z.any()),
  invoiceIncomeAccountSyncType: z.string().nullish(),
  billingContact: ContactRespSchema.nullish(),
  shippingContact: ContactRespSchema.nullish(),
  billingAddress: AddressSchema.nullish(),
  shippingAddress: AddressSchema.nullish(),
  voidReason: string().optional(),
  creditNoteMetadata: CreditNoteMetadataSchema.optional(),
});

export type ICreditNoteSchema = z.infer<typeof CreditNoteSchema>;

/**
 * Input schema - what the form accepts (before transformation)
 */
export const CreditNoteReqInputSchema = z.object({
  type: CreditNoteTypeEnumZ,
  amount: z
    .union([z.string(), z.number()])
    .refine(
      (val) => val !== null && val !== undefined && val !== '',
      getRequiredMessage('Amount'),
    ),
  refundable: boolean(),
  refundReason: CreditNoteRefundReasonEnumZ.optional(),
  otherReason: string().optional(),
  customFields: CustomFieldRecordSchema.nullable().optional(),
});

/**
 * Output schema - what the form produces (after transformation)
 */
export const CreditNoteReqOutputSchema = z.object({
  type: CreditNoteTypeEnumZ,
  amount: z.number(),
  refundable: boolean(),
  refundReason: CreditNoteRefundReasonEnumZ.optional(),
  otherReason: string().optional(),
  customFields: CustomFieldRecordSchema.nullable().optional(),
});

/**
 * Full schema with transformation
 */
export const CreditNoteReqSchema = CreditNoteReqInputSchema.transform(
  (data) => ({
    ...data,
    amount: Number(data.amount),
  }),
).pipe(CreditNoteReqOutputSchema);

export type ICreditNoteReqInput = z.infer<typeof CreditNoteReqInputSchema>;
export type ICreditNoteReqSchema = z.infer<typeof CreditNoteReqOutputSchema>;
